import React from "react";
import { useTranslation } from "react-i18next";
import PMSwitch from "../../../utils/PMSwitch";
import { PMDatePicker } from "../../../utils/PMDatePicker";
import { UserRelatedRecordsList, dsFieldCardStyle, dsLabelStyle, C } from "./user-related-records-list";

const CHILD_COLUMNS = [
  { id: "name", labelKey: "user_profile.children.field.name" },
  { id: "birth_date", labelKey: "user_profile.children.field.birth_date" },
  {
    id: "sole_custody",
    labelKey: "user_profile.children.field.sole_custody",
    renderValue: (row, t) => (row.sole_custody ? t("Yes") : t("No")),
  },
  {
    id: "extended_rights",
    labelKey: "user_profile.children.field.extended_rights",
    renderValue: (row, t) => (row.extended_rights ? t("Yes") : t("No")),
  },
];

const INITIAL_FORM = {
  name: "",
  birth_date: "",
  sole_custody: false,
  extended_rights: false,
};

const validateChildForm = (form, t) => {
  const errors = {};
  if (!String(form.name || "").trim()) {
    errors.name = t("user_profile.related_records.validation.name_required");
  }
  if (!String(form.birth_date || "").trim()) {
    errors.birth_date = t("user_profile.children.validation.birth_date_required");
  }
  return errors;
};

const prepareChildRow = (data, mode, existing) => {
  if (mode === "form") {
    return {
      name: data.name || "",
      birth_date: data.birth_date || "",
      sole_custody: !!data.sole_custody,
      extended_rights: !!data.extended_rights,
    };
  }
  return {
    id: existing?.id ?? null,
    name: String(data.name || "").trim(),
    birth_date: data.birth_date || "",
    sole_custody: !!data.sole_custody,
    extended_rights: !!data.extended_rights,
    active: true,
  };
};

const ChildFormFields = ({ formState, setFormState, formErrors, t }) => (
  <>
    <div>
      <label style={dsLabelStyle} htmlFor="child-birth-date">
        {t("user_profile.children.field.birth_date")}
      </label>
      <PMDatePicker
        triggerVariant="design"
        accentColor={C.accent}
        name="birth_date"
        value={formState.birth_date || ""}
        onChange={(e) =>
          setFormState((prev) => ({ ...prev, birth_date: e?.target?.value || "" }))
        }
        placeholder={t("Choose date")}
        errors={formErrors.birth_date}
        touched={Boolean(formErrors.birth_date)}
        validationRules={{
          maxDate: new Date(),
        }}
      />
    </div>

    <div>
      <label style={dsLabelStyle} htmlFor="child-name">
        {t("user_profile.children.field.name")}
      </label>
      <input
        id="child-name"
        type="text"
        value={formState.name || ""}
        onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
        style={{
          ...dsFieldCardStyle,
          borderColor: formErrors.name ? "#DC2626" : "#E2E8F0",
        }}
        placeholder={t("user_profile.children.field.name")}
      />
      {formErrors.name ? (
        <p style={{ margin: "6px 0 0", fontSize: 12, color: "#DC2626" }}>{formErrors.name}</p>
      ) : null}
    </div>

    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#334155" }}>
            {t("user_profile.children.field.sole_custody")}
          </p>
        </div>
        <PMSwitch
          name="sole_custody"
          checked={!!formState.sole_custody}
          onChange={(e) => setFormState((prev) => ({ ...prev, sole_custody: e.target.checked }))}
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#334155" }}>
            {t("user_profile.children.field.extended_rights")}
          </p>
        </div>
        <PMSwitch
          name="extended_rights"
          checked={!!formState.extended_rights}
          onChange={(e) => setFormState((prev) => ({ ...prev, extended_rights: e.target.checked }))}
        />
      </div>
    </div>
  </>
);

export default function UserChildrenTable(props) {
  const { t } = useTranslation();
  const { setFieldValue, values, isLoading, disabled: disabledProp, syncKey, onPersistRows } = props;
  const name = props.name || "user_children";

  return (
    <UserRelatedRecordsList
      name={name}
      syncKey={syncKey}
      setFieldValue={setFieldValue}
      values={values}
      isLoading={isLoading}
      disabled={disabledProp || values?.is_editable === false}
      columns={CHILD_COLUMNS}
      emptyTitleKey="user_profile.children.empty_title"
      emptyDescKey="user_profile.children.empty_desc"
      initialFormState={INITIAL_FORM}
      renderFormFields={(ctx) => <ChildFormFields {...ctx} t={t} />}
      validateForm={validateChildForm}
      prepareRow={prepareChildRow}
      onPersistRows={onPersistRows}
    />
  );
}

UserChildrenTable.defaultProps = {
  drag: false,
};
